import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useDocumentStatuses from '../../../hooks/useDocumentStatuses';

const DocumentTraceability = ({ id, isNew, projectID, projectName, reference, title, documentStatusID }) => {
    const [ traceability, setTraceability ] = useState(null);
    const [ traceabilityLoading, setTraceabilityLoading ] = useState(false);
    
    // Initialize collapsed states from localStorage
    const [ versionsCollapsed, setVersionsCollapsed ] = useState(() => {
        const stored = localStorage.getItem('documentTraceability.versionsCollapsed');
        return stored !== null ? stored === 'true' : false; // Default: open
    });
    const [ whereFromCollapsed, setWhereFromCollapsed ] = useState(() => {
        const stored = localStorage.getItem('documentTraceability.whereFromCollapsed');
        return stored !== null ? stored === 'true' : true; // Default: closed
    });
    const [ whereUsedCollapsed, setWhereUsedCollapsed ] = useState(() => {
        const stored = localStorage.getItem('documentTraceability.whereUsedCollapsed');
        return stored !== null ? stored === 'true' : true; // Default: closed
    });
    
    const {
        documentStatuses,
    } = useDocumentStatuses();
    
    // Toggle functions that also persist to localStorage
    const toggleVersions = () => {
        const newValue = !versionsCollapsed;
        setVersionsCollapsed(newValue);
        localStorage.setItem('documentTraceability.versionsCollapsed', newValue.toString());
    };
    
    const toggleWhereFrom = () => {
        const newValue = !whereFromCollapsed;
        setWhereFromCollapsed(newValue);
        localStorage.setItem('documentTraceability.whereFromCollapsed', newValue.toString());
    };
    
    const toggleWhereUsed = () => {
        const newValue = !whereUsedCollapsed;
        setWhereUsedCollapsed(newValue);
        localStorage.setItem('documentTraceability.whereUsedCollapsed', newValue.toString());
    };

    useEffect(() => {
        if( isNew ) {
            return;
        }
        setTraceabilityLoading(true);
        fetch('/v0/documents/' + id + '/traceability', { credentials: 'same-origin' })
            .then(function(response) {
                if (!response.ok) throw response;
                return response.json();
            })
            .then(function(json) {
                console.log("Document Get Traceability", json.result)
                setTraceability(json.result);
                setTraceabilityLoading(false);
            })
            .catch(error => {
                console.log("Document Get Traceability error", error);
                setTraceabilityLoading(false);
            });
    }, [id, isNew]);

    const renderVersions = () => {
        return (
            <div className="form-group">
                <label className="col-sm-2 control-label"></label>
                <div className="col-sm-8">
                    <label className="control-label" style={{cursor: 'pointer', userSelect: 'none'}} onClick={toggleVersions}>
                        <i className={`fa fa-chevron-${versionsCollapsed ? 'right' : 'down'}`} style={{marginRight: '8px', fontSize: '12px'}}></i>
                        Versions
                    </label>

                    {!versionsCollapsed && (traceabilityLoading ? (
                        <div className="m-t" style={{fontSize: '14px', color: '#676a6c'}}>
                            <i className="fa fa-spinner fa-spin"></i> Loading...
                        </div>
                    ) : traceability && traceability.versions ? (
                        <ul className="list-group clear-list m-t">
                            {traceability.versions.previous && traceability.versions.previous.slice().reverse().map((version) => (
                                <li key={version.id} className="list-group-item">
                                    <Link to={`/portfolio/${version.project_id}/documents/${version.id}`}>
                                        {version.reference} - {version.title}
                                    </Link>
                                </li>
                            ))}
                            <li className="list-group-item" style={{fontWeight: 'bold'}}>
                                {reference} - {title}
                            </li>
                            {traceability.versions.next && traceability.versions.next.map((version) => (
                                <li key={version.id} className="list-group-item">
                                    <Link to={`/portfolio/${version.project_id}/documents/${version.id}`}>
                                        {version.reference} - {version.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <ul className="list-group clear-list m-t">
                            <li className="list-group-item" style={{fontWeight: 'bold'}}>
                                {reference} - {title}
                            </li>
                        </ul>
                    ))}
                </div>
            </div>
        );
    };

    const renderWhereFrom = () => {
        return (
            <div className="form-group">
                <label className="col-sm-2 control-label"></label>
                <div className="col-sm-8">
                    <label className="control-label" style={{cursor: 'pointer', userSelect: 'none'}} onClick={toggleWhereFrom}>
                        <i className={`fa fa-chevron-${whereFromCollapsed ? 'right' : 'down'}`} style={{marginRight: '8px', fontSize: '12px'}}></i>
                        Where From
                    </label>

                    {!whereFromCollapsed && (traceabilityLoading ? (
                        <div className="m-t" style={{fontSize: '14px', color: '#676a6c'}}>
                            <i className="fa fa-spinner fa-spin"></i> Loading...
                        </div>
                    ) : traceability && traceability.where_from && traceability.where_from.previous && traceability.where_from.previous.length > 0 ? (
                        <div className="table-responsive m-t">
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>Project Name</th>
                                        <th>Status</th>
                                        <th>Reference & Title</th>
                                        <th>Document Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const rows = [];
                                        const versions = traceability.where_from.previous.slice().reverse();
                                        
                                        let indent = 0;
                                        // Helper function to calculate status text and indent
                                        const getStatusInfo = (version, prevVersion) => {
                                            let statusText = '';
                                            
                                            if (!prevVersion) {
                                                statusText = 'first version';
                                            } else {
                                                if (prevVersion.from_included_doc) {
                                                    statusText = 'forked';
                                                    indent += 1;
                                                } else if (version.project_id !== prevVersion.project_id) {
                                                    statusText = 'included';
                                                    indent = 1;
                                                } else if (version.issue_number > prevVersion.issue_number) {
                                                    statusText = 'upissue';
                                                } else if (version.revision !== prevVersion.revision) {
                                                    statusText = 'uprevision';
                                                }
                                            }
                                            
                                            return { statusText, indent };
                                        };
                                        
                                        versions.forEach((version, index) => {
                                            const prevVersion = index === 0 ? null : versions[index - 1];
                                            const { statusText, indent } = getStatusInfo(version, prevVersion);
                                            const statusName = documentStatuses.find(s => s.id === version.document_status_id)?.name || '';
                                            const paddingLeft = indent * 20;
                                            
                                            rows.push(
                                                <tr key={version.id}>
                                                    <td>{version.project_name}</td>
                                                    <td>{statusText}</td>
                                                    <td style={{paddingLeft: `${paddingLeft}px`}}>
                                                        <Link to={`/portfolio/${version.project_id}/documents/${version.id}`}>
                                                            {version.reference} - {version.title}
                                                        </Link>
                                                    </td>
                                                    <td>{statusName}</td>
                                                </tr>
                                            );
                                        });
                                        
                                        // Add current document as the last row (bold)
                                        if (traceability.current) {
                                            const current = traceability.current;
                                            const lastVersion = versions.length > 0 ? versions[versions.length - 1] : null;
                                            const { statusText, indent } = getStatusInfo(current, lastVersion);
                                            const statusName = documentStatuses.find(s => s.id === current.document_status_id)?.name || '';
                                            const paddingLeft = indent * 20;
                                            
                                            rows.push(
                                                <tr key={current.id}>
                                                    <td>{current.project_name}</td>
                                                    <td>{statusText}</td>
                                                    <td style={{fontWeight: 'bold', paddingLeft: `${paddingLeft}px`}}>
                                                        {current.reference} - {current.title}
                                                    </td>
                                                    <td>{statusName}</td>
                                                </tr>
                                            );
                                        }
                                        
                                        return rows;
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="table-responsive m-t">
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>Project Name</th>
                                        <th>Status</th>
                                        <th>Reference & Title</th>
                                        <th>Document Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {traceability && traceability.current && (
                                        <tr>
                                            <td>{traceability.current.project_name}</td>
                                            <td>first version</td>
                                            <td style={{fontWeight: 'bold'}}>
                                                {traceability.current.reference} - {traceability.current.title}
                                            </td>
                                            <td>{documentStatuses.find(s => s.id === traceability.current.document_status_id)?.name || ''}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderWhereUsed = () => {
        return (
            <div className="form-group">
                <label className="col-sm-2 control-label"></label>
                <div className="col-sm-8">
                    <label className="control-label" style={{cursor: 'pointer', userSelect: 'none'}} onClick={toggleWhereUsed}>
                        <i className={`fa fa-chevron-${whereUsedCollapsed ? 'right' : 'down'}`} style={{marginRight: '8px', fontSize: '12px'}}></i>
                        Where Used
                    </label>

                    {!whereUsedCollapsed && (traceabilityLoading ? (
                        <div className="m-t" style={{fontSize: '14px', color: '#676a6c'}}>
                            <i className="fa fa-spinner fa-spin"></i> Loading...
                        </div>
                    ) : traceability && traceability.where_used && traceability.where_used.usage ? (
                        <div className="table-responsive m-t">
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>Project Name</th>
                                        <th>Status</th>
                                        <th>Reference & Title</th>
                                        <th>Document Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const rows = [];
                                        
                                        // Helper function to calculate status text for where_used
                                        const getWhereUsedStatusInfo = (doc, prevDoc, isIncluded, isForked) => {
                                            let statusText = '';
                                            
                                            if (!prevDoc) {
                                                statusText = ''; // Current document has no status in where_used
                                            } else if (isForked) {
                                                statusText = 'forked';
                                            } else if (isIncluded) {
                                                statusText = 'included';
                                            } else if (doc.issue_number > prevDoc.issue_number) {
                                                statusText = 'upissue';
                                            } else if (doc.revision !== prevDoc.revision) {
                                                statusText = 'uprevision';
                                            }
                                            
                                            return statusText;
                                        };
                                        
                                        // Recursive function to traverse the usage tree
                                        const traverseUsageNode = (node, prevDoc, currentIndent, isIncluded = false, isForked = false) => {
                                            const doc = node.document;
                                            const statusText = getWhereUsedStatusInfo(doc, prevDoc, isIncluded, isForked);
                                            const statusName = documentStatuses.find(s => s.id === doc.document_status_id)?.name || '';
                                            const paddingLeft = currentIndent * 20;
                                            const isCurrent = !prevDoc; // First one is current
                                            
                                            rows.push(
                                                <tr key={`doc-${doc.id}`}>
                                                    <td>{doc.project_name}</td>
                                                    <td>{statusText}</td>
                                                    <td style={isCurrent ? {fontWeight: 'bold', paddingLeft: `${paddingLeft}px`} : {paddingLeft: `${paddingLeft}px`}}>
                                                        <Link to={`/portfolio/${doc.project_id}/documents/${doc.id}`}>
                                                            {doc.reference} - {doc.title}
                                                        </Link>
                                                    </td>
                                                    <td>{statusName}</td>
                                                </tr>
                                            );
                                            
                                            // Process next version (same indent level)
                                            if (node.next_version) {
                                                traverseUsageNode(node.next_version, doc, currentIndent, false, false);
                                            }
                                            
                                            // Process included documents (indent +1)
                                            if (node.included_in && node.included_in.length > 0) {
                                                node.included_in.forEach(includedUsage => {
                                                    const incDoc = includedUsage.included_document;
                                                    const incStatusName = documentStatuses.find(s => s.id === incDoc.document_status_id)?.name || '';
                                                    const incStatusText = getWhereUsedStatusInfo(incDoc, doc, true, false);
                                                    const incIndent = currentIndent + 1;
                                                    const incPaddingLeft = incIndent * 20;
                                                    
                                                    rows.push(
                                                        <tr key={`inc-${incDoc.id}`}>
                                                            <td>{incDoc.project_name}</td>
                                                            <td>{incStatusText}</td>
                                                            <td style={{paddingLeft: `${incPaddingLeft}px`}}>
                                                                <Link to={`/portfolio/${incDoc.project_id}/included_documents/${incDoc.id}`}>
                                                                    {incDoc.reference} - {incDoc.title}
                                                                </Link>
                                                            </td>
                                                            <td>{incStatusName}</td>
                                                        </tr>
                                                    );
                                                    
                                                    // Process forked documents from this included document (indent +1 from included)
                                                    if (includedUsage.forked_documents && includedUsage.forked_documents.length > 0) {
                                                        includedUsage.forked_documents.forEach(forkedNode => {
                                                            traverseUsageNode(forkedNode, incDoc, incIndent + 1, false, true);
                                                        });
                                                    }
                                                });
                                            }
                                        };
                                        
                                        // Start traversal from the root usage node with indent 0
                                        traverseUsageNode(traceability.where_used.usage, null, 0);
                                        
                                        return rows;
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="table-responsive m-t">
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>Project Name</th>
                                        <th>Status</th>
                                        <th>Reference & Title</th>
                                        <th>Document Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {traceability && traceability.current && (
                                        <tr>
                                            <td>{traceability.current.project_name}</td>
                                            <td></td>
                                            <td style={{fontWeight: 'bold'}}>
                                                {traceability.current.reference} - {traceability.current.title}
                                            </td>
                                            <td>{documentStatuses.find(s => s.id === traceability.current.document_status_id)?.name || ''}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    if (isNew) {
        return null;
    }

    return (
        <>
            <div className="hr-line-dashed"></div>
            {renderVersions()}
            {renderWhereFrom()}
            {renderWhereUsed()}
            <div className="hr-line-dashed"></div>
        </>
    );
};

export default DocumentTraceability;
